import type { ReactNode } from 'react';
import type { Snapshot } from '../types';
import { classColor, factionColor, formatMoney, isStale, specOf } from '../lib/wow';
import { averageItemLevel } from '../lib/ilvl';

interface Props {
  snapshot: Snapshot;
  updatedAt: number;
  right?: ReactNode;
}

export function Header({ snapshot, updatedAt, right }: Props) {
  const p = snapshot.profile;
  const color = classColor(p.classFile);
  const fc = factionColor(p.faction);
  const ago = timeAgo(updatedAt);
  const stale = isStale(updatedAt);
  const spec = specOf(snapshot);
  const ilvl = averageItemLevel(snapshot);

  // XP bar (only meaningful below 80 in WotLK).
  const xp = p.xp ?? 0;
  const xpMax = p.xpMax ?? 0;
  const xpPct = xpMax > 0 ? Math.min(100, Math.max(0, (xp / xpMax) * 100)) : 0;
  const showXp = p.level < 80 && xpMax > 0;

  return (
    <header className="char-header" style={{ borderImage: `linear-gradient(90deg, ${fc}88, transparent 60%) 1` }}>
      <div className="char-header-top">
        <div className="char-header-name-block">
          <div className="char-name" style={{ color }}>
            {p.name}
            {stale && <span className="chip stale" title={`Last updated ${ago}`}> stale</span>}
          </div>
          <div className="char-sub">
            <span>Level {p.level}</span>
            <span>{p.race}</span>
            <span style={{ color }}>
              {spec?.iconUrl && (
                <img src={spec.iconUrl} alt="" className="spec-icon-inline" />
              )}
              {spec ? `${spec.name} ${p.class}` : p.class}
            </span>
            {p.guild && <span className="guild">&lt;{p.guild}&gt;</span>}
            <span>· {p.realm}</span>
            <span style={{ color: fc }}>· {p.faction}</span>
          </div>
        </div>
        {right && <div className="char-header-right">{right}</div>}
      </div>

      <div className="char-meta">
        {p.zone && <span>📍 {p.zone}{p.subZone ? ` — ${p.subZone}` : ''}</span>}
        <span>💰 {formatMoney(p.money)}</span>
        <span title={ilvl == null ? 'Average iLvl not yet captured by the addon' : 'Average equipped item level'}>
          ⚙ iLvl {ilvl == null ? '—' : ilvl}
        </span>
        <span className="updated">updated {ago}</span>
      </div>

      {showXp && (
        <div
          className="xp-bar"
          title={`${xp.toLocaleString()} / ${xpMax.toLocaleString()} XP (${xpPct.toFixed(1)}%)`}
        >
          <div className="xp-bar-fill" style={{ width: `${xpPct}%` }} />
        </div>
      )}
    </header>
  );
}

function timeAgo(ms: number): string {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
